import { YStack } from 'tamagui';

import type { Membership } from '@/src/api/membershipsApi';
import {
  hasPlansOnOffer,
  type MemberState,
  type MembershipUpsell,
} from '@/src/lib/membershipUpsell';
import { formatDateWithYear } from '@/src/utils/dateFormatter';
import MembershipPlanCard from './MembershipPlanCard';
import PSText from './ui/PSText';

// Not on the wire: the plan's `description` is the card's copy, not this line.
const TAGLINE: Record<Membership['kind'], string> = {
  trial:
    'Enjoy two events during your 30-day trial, each with a little something on us, such as a glass of wine at a kitchen takeover or dessert at a listening session.',
  access_pass:
    'Unlimited access to everything we host for members, such as a preview before a restaurant opens, a drink on the house at the city’s best events, or a seat saved for you at a sold out event.',
};

const eyebrowFor = (state: MemberState) => {
  switch (state.kind) {
    case 'trial-ended':
      return 'Enjoyed your trial?';
    case 'on-trial':
      return state.perksUsedUp ? 'Enjoyed your trial?' : 'Upgrade';
    case 'member':
      return 'Upgrade';
    case 'lapsing':
      return 'Keep your membership';
    case 'ex-member':
      return 'Rejoin the inner circle:';
    default:
      return 'Start now';
  }
};

/**
 * The plans [[deriveMembershipUpsell]] puts on offer, drawn. Shared by home's
 * Access Pass block and the Access Pass page's plans section. White text: both
 * surfaces are `$brandDark`.
 */
export default function MembershipUpsellSection({
  upsell,
  withFeatures = false,
  onSelectMembership,
}: {
  upsell: MembershipUpsell;
  /** Passed to every card: the perk rows, for a surface that invites comparison. */
  withFeatures?: boolean;
  onSelectMembership: (plan: Membership) => void;
}) {
  const { state, renewablePlan, membershipsToUpsell } = upsell;
  if (!hasPlansOnOffer(upsell)) return null;

  const current = 'membership' in state ? state.membership : null;
  // Keyed off the date, not the status: a cancellation and a coupon grant both
  // run out. A renewing subscription has no `expiresAt`.
  const subline =
    state.kind === 'trial-ended'
      ? 'Upgrade to:'
      : state.kind === 'on-trial' && state.perksUsedUp
        ? `You've used up your ${state.membership.plan.name} perks. Upgrade: `
        : current?.expiresAt
          ? `Your ${current.plan.name} benefits end on ${formatDateWithYear(current.expiresAt)}`
          : null;

  const block = (plan: Membership, ctaLabel: string) => (
    <YStack key={plan.id} gap={'$space.md'}>
      <YStack gap={'$space.xs'}>
        <PSText.Title
          color={'$color.textWhite'}
        >{`The ${plan.name}`}</PSText.Title>
        <PSText.Body2 color={'$color.textWhite'}>
          {TAGLINE[plan.kind]}
        </PSText.Body2>
      </YStack>
      <MembershipPlanCard
        plan={plan}
        withFeatures={withFeatures}
        ctaLabel={ctaLabel}
        onPress={() => onSelectMembership(plan)}
      />
    </YStack>
  );

  return (
    <YStack gap={'$space.xl'}>
      <YStack gap={'$space.xs'}>
        <PSText.H3 color={'$color.brandSecondary'}>
          {eyebrowFor(state)}
        </PSText.H3>
        {subline && (
          <PSText.Body2 color={'$color.textWhiteMuted'}>{subline}</PSText.Body2>
        )}
      </YStack>

      {renewablePlan &&
        block(
          renewablePlan,
          withFeatures ? 'Renew Now' : `Renew ${renewablePlan.name}`
        )}
      {membershipsToUpsell.map((plan) =>
        block(plan, withFeatures ? 'Buy Now' : `Explore ${plan.name}`)
      )}
    </YStack>
  );
}
